import {useEffect, useRef, useState} from 'react'
import {publish, subscribe, unsubscribe} from '@/script/event.mjs'
import Tree from '@/script/Tree.mjs'
import parseMap from '../scripts/parseMap.mjs'

export default () => {
    const [isOn, setIsOn] = useState(false)
    const refCidToNid = useRef(new Map)
    const refHistory = useRef(new Map)
    const refIsToggling = useRef(false)
    const refNodeDiff = useRef(new Map)
    const refNidToCid = useRef(new Map)

    const _off = map => {
        setIsOn(false)
        refCidToNid.current = new Map
        refNidToCid.current = new Map

        for (const nodeId of [...refNodeDiff.current.keys()]) {
            map.deleteNodeDiff(nodeId)
        }
    }

    const _on = (map, {cidToNid, nidToCid, nodeDiff}) => {
        setIsOn(true)
        refCidToNid.current = new Map(cidToNid)
        refNidToCid.current = new Map(nidToCid)

        for (const [node, diff] of [...nodeDiff.entries()]) {
            map.setNodeDiff(node.id, diff)
        }
    }

    const extensions = map => {
        const importTree = map.importTree.bind(map)

        return {
            compareNode(a, b) {
                const {bizNodeType} = a.data
                const {Model} = this.BizNode[bizNodeType]
                const model = new Model(this)
                const modifications = []

                const verA = `${a.sVer}.${a.rev}`
                const verB = `${b.sVer}.${b.rev}`

                if (verA !== verB) {
                    modifications.push(['版本号', verA, verB])
                }

                const getValue = (node, prop) => {
                    const value = model.readableValue(node, prop)

                    if (value || 0 === value) {
                        return value
                    }
                    else {
                        return '(空)'
                    }
                }

                for (const prop of Object.keys(Model.definition)) {
                    const valueA = getValue(a, prop)
                    const valueB = getValue(b, prop)

                    if (valueA !== valueB) {
                        const name = model.name(a, prop)
                        modifications.push([name, valueA, valueB])
                    }
                }

                return modifications
            },


            deleteNodeDiff(nodeId) {
                refNodeDiff.current.delete(nodeId)
                const node = this.getNode(nodeId)
                publish(node, 'node_diff_change', [])
            },

            async diffModeOff() {
                try {
                    refIsToggling.current = true

                    await this.execute(() => {
                        for (
                            const [nodeId, [type]] of
                            [...refNodeDiff.current.entries()]
                        ) {
                            if ('D' === type) {
                                const node = this.getNode(nodeId)
                                this.deleteTree(node)
                            }
                        }
                    })
                }
                finally {
                    refIsToggling.current = false
                }

                _off(this)
            },

            async diffModeOn(leftJson, rightJson, changeList) {
                try {
                    refIsToggling.current = true

                    {
                        const mapData = parseMap(leftJson)
                        const tree = new Tree(mapData.root)
                        const itTree = tree.dfs(tree.root)

                        const next = chain => {
                            const [n] = chain
                            const {bizNodeType} = n.data
                            const BN = this.BizNode[bizNodeType]
                            const isHidden = BN.isHidden(this, n)
                            const isAttr = BN.classes.includes('attribute')

                            return {
                                yieldChildren: ! isHidden,
                                yieldNode: ! (isAttr || isHidden),
                            }
                        }

                        const itMap = this.dfs(this.root, next)

                        while (true) {
                            const {done, value} = itTree.next()
                            const {value: node} = itMap.next()

                            if (done) {
                                break
                            }

                            const {mapCompUuid} = value.data

                            if (mapCompUuid) {
                                refCidToNid.current.set(mapCompUuid, node.id)
                                refNidToCid.current.set(node.id, mapCompUuid)
                            }
                        }
                    }

                    const cidToNode = new Map
                    const nodeToCid = new Map

                    {
                        const mapData = parseMap(rightJson)

                        new Tree(mapData.root, node => {
                            const {mapCompUuid, ...data} = node.data
                            node.data = data

                            if (mapCompUuid) {
                                cidToNode.set(mapCompUuid, node)
                                nodeToCid.set(node, mapCompUuid)
                            }
                        })
                    }

                    await this.execute(() => {
                        for (
                            const {changeType, mapCompUuid: cid}
                            of changeList
                        ) {
                            ({
                                ADD: () => {
                                    const n = cidToNode.get(cid)
                                    const pcid = nodeToCid.get(n.parent)
                                    const parent = this.getNodeByCmpId(pcid)
                                    const node = this.importTree(n.export())
                                    this.appendChild(parent, node)
                                    this.setNodeDiff(node.id, ['D'])
                                },

                                DEL: () => {
                                    const node = this.getNodeByCmpId(cid)
                                    this.setNodeDiff(node.id, ['A'])
                                },

                                UPT: () => {
                                    const n = cidToNode.get(cid)
                                    const node = this.getNodeByCmpId(cid)
                                    const mods = this.compareNode(n, node)
                                    this.setNodeDiff(node.id, ['M', mods])
                                },
                            })[changeType]()
                        }
                    })
                }
                finally {
                    refIsToggling.current = false
                }

                setIsOn(true)
            },

            getCmpIdByNodeId(nodeId) {
                return refNidToCid.current.get(nodeId)
            },

            getNodeByCmpId(cmpId) {
                const nid = refCidToNid.current.get(cmpId)
                return nid ? this.getNode(nid) : void 0
            },

            getNodeDiff(nodeId) {
                return refNodeDiff.current.get(nodeId) ?? []
            },

            isDiffModeOn: isOn,

            setNodeDiff(nodeId, [type, extra]) {
                refNodeDiff.current.set(nodeId, [type, extra])
                const node = this.getNode(nodeId)
                publish(node, 'node_diff_change', [type, extra])
            },

            /**
             * @override
             */
            importTree(tree, onImport) {
                return importTree(tree, node => {
                    const {
                        mapCompUuid,
                        mapCompDppCode,
                        mapCompDppId,
                        mapCompPuuid,
                        ...data
                    } = node.data

                    node.data = data

                    if (mapCompUuid) {
                        refCidToNid.current.set(mapCompUuid, node.id)
                        refNidToCid.current.set(node.id, mapCompUuid)
                    }

                    onImport?.(node)
                })
            },

            useNodeDiff(node) {
                const [diff, setDiff] = useState(
                    () => this.getNodeDiff(node.id)
                )

                useEffect(
                    () => {
                        subscribe(node, 'node_diff_change', setDiff)

                        return () => {
                            unsubscribe(node, 'node_diff_change', setDiff)
                        }
                    },

                    [node]
                )

                return diff
            },

            /**
             * @override
             */
            useNodeBoxStyle(node) {
                this.useNodeChange(node, ['mode']) // TEMP

                const [style, setStyle] = useState(
                    () => this.getNodeBoxStyle(node)
                )

                useEffect(
                    () => setStyle(this.getNodeBoxStyle(node)),
                    [node.mode]
                )

                useEffect(
                    () => {
                        const onDiffChange = () => {
                            setStyle(this.getNodeBoxStyle(node))
                        }

                        subscribe(node, 'node_diff_change', onDiffChange)

                        return () => {
                            unsubscribe(node, 'node_diff_change', onDiffChange)
                        }
                    },

                    [node]
                )

                return style
            },
        }
    }

    const watchers = {
        history_redo(id) {
            const state = refHistory.current.get(id)

            if (! state) {
                return
            }

            if (state.isOn) {
                _off(this)
            }
            else {
                _on(this, state)
            }
        },

        history_push(id) {
            if (! refIsToggling.current) {
                return
            }

            const cidToNid = new Map(refCidToNid.current)
            const nidToCid = new Map(refNidToCid.current)
            const nodeDiff = new Map(refNodeDiff.current)

            refHistory.current.set(id, {
                cidToNid,
                isOn,
                nidToCid,
                nodeDiff,
            })
        },

        history_undo(id) {
            const state = refHistory.current.get(id)

            if (! state) {
                return
            }

            if (state.isOn) {
                _on(this, state)
            }
            else {
                _off(this)
            }
        },
    }

    return {extensions, watchers}
}
